import React, { useState } from 'react';
import './ShareModal.scss';
import { QRCodeCanvas } from "qrcode.react";

import instagramIcon from '../../assets/icons/instagram.svg';
import facebookIcon from '../../assets/icons/facebook.svg';
import xIcon from '../../assets/icons/x.svg';

interface IProps {
  eventTitle: string;
  eventUrl: string;
  shareType?: 'event' | 'club' | 'dog'; // Added 'dog' type
  onClose: () => void;
}

const ShareModal: React.FC<IProps> = ({ eventTitle, eventUrl, shareType = 'event', onClose }) => {
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(eventUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleSocialShare = (platform: string) => {
    let shareText = '';
    
    // Create share text based on type
    switch (shareType) {
      case 'dog':
        shareText = `Check out ${eventTitle} on Eagerdog! 🐕`;
        break;
      case 'club':
        shareText = `Check out this kennel club: ${eventTitle}`;
        break;
      case 'event':
      default:
        shareText = `Check out this dog event: ${eventTitle}`;
        break;
    }
    
    let shareUrl = '';

    switch (platform) {
      case 'facebook':
        // Facebook Sharer - will automatically use og:title, og:description, og:image from meta tags
        // Cannot pre-fill message due to Facebook policy, but the meta tags will populate everything
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(eventUrl)}&quote=${encodeURIComponent(shareText)}`;
        break;
      case 'x':
        // X (Twitter) Intent URL - CAN pre-fill text
        const hashtags = shareType === 'dog' ? 'DogSports,Eagerdog' : 'DogEvents,DogShows';
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(eventUrl)}&hashtags=${hashtags}`;
        break;
      case 'instagram':
        // Instagram doesn't support URL sharing via web, so copy link to clipboard
        handleCopyLink();
        alert('Link copied! Open Instagram and paste it in your story or post.');
        return;
      default:
        return;
    }

    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="ShareModal">
      <div className="content">
        <h2>
          {shareType === 'dog' 
            ? 'Share this dog profile with your friends!' 
            : 'Share your show with your friends on social media!'}
        </h2>
        
        <div className="socialIcons">
          <button 
            className="socialIcon instagram" 
            onClick={() => handleSocialShare('instagram')}
            title="Share on Instagram"
          >
            <img src={instagramIcon} alt="Instagram" />
          </button>
          <button 
            className="socialIcon facebook" 
            onClick={() => handleSocialShare('facebook')}
            title="Share on Facebook"
          >
            <img src={facebookIcon} alt="Facebook" />
          </button>
          <button 
            className="socialIcon x" 
            onClick={() => handleSocialShare('x')}
            title="Share on X"
          >
            <img src={xIcon} alt="X" />
          </button>
        </div>

        <div>
          <QRCodeCanvas
            value={eventUrl}
            size={200}
            level="H"
          />
        </div>

        <div className="linkSection">
          <div className="linkLabel">Page Link</div>
          <div className="linkContainer">
            <input 
              type="text" 
              value={eventUrl} 
              readOnly 
              className="linkInput"
            />
            <button 
              className="copyBtn" 
              onClick={handleCopyLink}
              title="Copy link"
            >
              {copySuccess ? '✓' : '📋'}
            </button>
          </div>
          {copySuccess && <div className="copyMessage">Link copied!</div>}
        </div>
      </div>
    </div>
  );
};

export default ShareModal; 